import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import type { IconName } from 'lucide-react/dynamic';
import { useEffect } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { TERM_DOCUMENT_CSS } from '@/lib/terms/document';
import { termExtensions } from '@/lib/terms/extensions';
import { TERM_VARIABLES } from '@/lib/terms/variables';
import { cn } from '@/lib/utils';

type TermDocument = Record<string, unknown>;

interface TermEditorProps {
  content: TermDocument;
  onChange?: (content: TermDocument) => void;
  readOnly?: boolean;
}

/** Variáveis do responsável — agrupadas à parte no menu (só fazem sentido na autorização). */
const GUARDIAN_PREFIX = 'responsavel_';

/**
 * Editor dos termos: folha A4 (210mm) com o MESMO CSS do PDF (`TERM_DOCUMENT_CSS`) e as MESMAS
 * extensões do `generateHTML` do servidor (`termExtensions`) — o que se vê aqui é o que sai impresso.
 * Variáveis entram só pelo menu (chips), nunca digitadas.
 */
export function TermEditor({ content, onChange, readOnly = false }: TermEditorProps) {
  const editor = useEditor({
    extensions: termExtensions,
    content,
    editable: !readOnly,
    immediatelyRender: true,
    editorProps: {
      attributes: {
        class: 'term-document outline-none',
        'aria-label': 'Conteúdo do termo',
      },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getJSON()),
  });

  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-[14px] border border-border bg-sunken">
      <style>{TERM_DOCUMENT_CSS}</style>
      {!readOnly && editor && <Toolbar editor={editor} />}
      {/* A folha rola dentro do card, como na pré-visualização — a barra de estilos fica sempre visível. */}
      <div className="max-h-[calc(100dvh-200px)] min-h-[420px] overflow-auto p-4 md:p-8">
        <div className="mx-auto w-fit shadow-[var(--shadow-float)]">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
}

type ToolbarEditor = NonNullable<ReturnType<typeof useEditor>>;

function Toolbar({ editor }: { editor: ToolbarEditor }) {
  // Re-renderiza a barra quando a seleção muda (estado ativo dos botões).
  const state = useEditorState({
    editor,
    selector: ({ editor }) => ({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      underline: editor.isActive('underline'),
      h1: editor.isActive('heading', { level: 1 }),
      h2: editor.isActive('heading', { level: 2 }),
      bullet: editor.isActive('bulletList'),
      ordered: editor.isActive('orderedList'),
      left: editor.isActive({ textAlign: 'left' }),
      center: editor.isActive({ textAlign: 'center' }),
      right: editor.isActive({ textAlign: 'right' }),
      justify: editor.isActive({ textAlign: 'justify' }),
      canUndo: editor.can().undo(),
      canRedo: editor.can().redo(),
    }),
  });

  const chain = () => editor.chain().focus();

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border bg-card px-3 py-2.5">
      <ToolButton
        icon="undo-2"
        label="Desfazer"
        disabled={!state.canUndo}
        onClick={() => chain().undo().run()}
      />
      <ToolButton
        icon="redo-2"
        label="Refazer"
        disabled={!state.canRedo}
        onClick={() => chain().redo().run()}
      />
      <Divider />
      <ToolButton
        icon="heading-1"
        label="Título"
        active={state.h1}
        onClick={() => chain().toggleHeading({ level: 1 }).run()}
      />
      <ToolButton
        icon="heading-2"
        label="Subtítulo"
        active={state.h2}
        onClick={() => chain().toggleHeading({ level: 2 }).run()}
      />
      <Divider />
      <ToolButton
        icon="bold"
        label="Negrito"
        active={state.bold}
        onClick={() => chain().toggleBold().run()}
      />
      <ToolButton
        icon="italic"
        label="Itálico"
        active={state.italic}
        onClick={() => chain().toggleItalic().run()}
      />
      <ToolButton
        icon="underline"
        label="Sublinhado"
        active={state.underline}
        onClick={() => chain().toggleUnderline().run()}
      />
      <Divider />
      <ToolButton
        icon="list"
        label="Lista"
        active={state.bullet}
        onClick={() => chain().toggleBulletList().run()}
      />
      <ToolButton
        icon="list-ordered"
        label="Lista numerada"
        active={state.ordered}
        onClick={() => chain().toggleOrderedList().run()}
      />
      <Divider />
      <ToolButton
        icon="align-left"
        label="Alinhar à esquerda"
        active={state.left}
        onClick={() => chain().setTextAlign('left').run()}
      />
      <ToolButton
        icon="align-center"
        label="Centralizar"
        active={state.center}
        onClick={() => chain().setTextAlign('center').run()}
      />
      <ToolButton
        icon="align-right"
        label="Alinhar à direita"
        active={state.right}
        onClick={() => chain().setTextAlign('right').run()}
      />
      <ToolButton
        icon="align-justify"
        label="Justificar"
        active={state.justify}
        onClick={() => chain().setTextAlign('justify').run()}
      />
      <Divider />
      <ToolButton
        icon="minus"
        label="Linha divisória"
        onClick={() => chain().setHorizontalRule().run()}
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="ml-auto flex h-8 items-center gap-2 rounded-[9px] border border-olive-line/40 bg-accent px-3 text-[12.5px] font-semibold text-olive-ink transition-colors hover:border-olive-line"
          >
            <Icon name="braces" className="size-4" />
            Inserir variável
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-[360px] w-64 overflow-y-auto">
          <DropdownMenuLabel className="col-label text-faint">
            Participante e evento
          </DropdownMenuLabel>
          {TERM_VARIABLES.filter((v) => !v.name.startsWith(GUARDIAN_PREFIX)).map((v) => (
            <VariableItem key={v.name} name={v.name} label={v.label} editor={editor} />
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="col-label text-faint">
            Responsável (menores)
          </DropdownMenuLabel>
          {TERM_VARIABLES.filter((v) => v.name.startsWith(GUARDIAN_PREFIX)).map((v) => (
            <VariableItem key={v.name} name={v.name} label={v.label} editor={editor} />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function VariableItem({
  name,
  label,
  editor,
}: {
  name: string;
  label: string;
  editor: ToolbarEditor;
}) {
  return (
    <DropdownMenuItem
      onSelect={() => editor.chain().focus().insertVariable(name).run()}
      className="flex flex-col items-start gap-0.5"
    >
      <span className="text-[13px] text-foreground">{label}</span>
      <span className="font-mono text-[11px] text-faint">{`{{${name}}}`}</span>
    </DropdownMenuItem>
  );
}

function ToolButton({
  icon,
  label,
  active,
  disabled,
  onClick,
}: {
  icon: IconName;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex size-8 items-center justify-center rounded-[9px] border transition-colors disabled:opacity-40',
        active
          ? 'border-olive-line/40 bg-accent text-olive-ink'
          : 'border-transparent text-muted-foreground hover:border-border hover:bg-raised hover:text-foreground',
      )}
    >
      <Icon name={icon} className="size-4" />
    </button>
  );
}

const Divider = () => <span className="mx-1 h-5 w-px bg-border" aria-hidden />;
